<script>
import { cloneDeep, get } from "lodash-es";
import { mapStores } from "pinia";

import lang from "@/directives/lang";
import { useInflectionSchemeStore } from "@/stores";

export default {
  name: "VerbInflectionSchemeModal",
  directives: {
    lang
  },
  props: {
    lemma: {
      type: String,
      required: true
    },
    inflectionScheme: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeTab: { key: -1, label: undefined }, // initialized on mount
      simpleSchemeToInflectionSchemeKeyMap: {
        present: "indicative.present",
        past: "indicative.simple_past",
        present_perfect: "perfect_tenses.present_perfect",
        present_continuous: "continuous_tenses.present_continuous",
        future: "indicative.future",
        past_perfect: "perfect_tenses.past_perfect"
      },
      personOrder: [
        "first_person_singular",
        "second_person_singular",
        "third_person_singular",
        "first_person_plural",
        "second_person_plural",
        "third_person_plural"
      ]
    };
  },
  computed: {
    ...mapStores(useInflectionSchemeStore),

    inflectionLanguage() {
      return this["inflection-schemeStore"].inflectionSchemeLanguage;
    },

    simpleSchemes() {
      const simpleSchemes = {};

      Object.entries(this.simpleSchemeToInflectionSchemeKeyMap).forEach(
        ([simpleSchemeKey, inflectionSchemeKey]) => {
          const scheme = cloneDeep(get(this.inflectionScheme, inflectionSchemeKey));
          if (scheme) {
            // Remove "array" part of the data structure, as it is redundant
            Object.keys(scheme).forEach((key) => {
              scheme[key] = scheme[key][0];
            });

            // Add third_person_singular key, combining existing third_person_{he|she|it}_singular keys
            scheme.third_person_singular = {
              ...scheme.third_person_it_singular,
              person: "he/she/it"
            };

            simpleSchemes[simpleSchemeKey] = this.filterAndSortScheme(scheme);
          }
        }
      );

      return simpleSchemes;
    },
    tabs() {
      return Object.keys(this.simpleSchemes).map((key) => this.createTab(key));
    }
  },
  mounted() {
    this.changeTab(this.tabs[0]);
  },
  methods: {
    changeTab(tab) {
      this.activeTab = tab;
    },
    createTab(key) {
      const map = {
        present: "Nutid",
        past: "Datid",
        present_perfect: "Førnutid",
        present_continuous: "Udvidet tid",
        future: "Fremtid",
        past_perfect: "Førdatid"
      };

      return { key, label: map[key] };
    },
    filterAndSortScheme(scheme) {
      return Object.keys(scheme)
        .filter((key) => this.personOrder.includes(key))
        .sort((a, b) => {
          const indexA = this.personOrder.indexOf(a);
          const indexB = this.personOrder.indexOf(b);
          return indexA < indexB ? -1 : 1;
        })
        .map((key) => scheme[key]);
    }
  }
};
</script>

<template>
  <div>
    <div class="inflection-scheme-modal__tabs">
      <template v-for="tab in tabs" :key="tab.key">
        <button
          class="inflection-scheme-modal__tab-button"
          :class="{ 'inflection-scheme-modal__tab-button--active': activeTab.key === tab.key }"
          @click="changeTab(tab)"
        >
          {{ tab.label }}
        </button>
      </template>
    </div>
    <table class="table inflection-scheme-modal__table">
      <caption class="sr-only">
        {{
          $t("article.inflectionScheme.caption", { word: lemma })
        }}
      </caption>

      <tbody v-lang:[inflectionLanguage]>
        <template v-for="(row, key) in simpleSchemes[activeTab.key]" :key="key">
          <tr
            class="inflection-scheme-modal__tr"
            :class="{
              'inflection-scheme-modal__tr--singular-last':
                key === personOrder.indexOf('third_person_singular'),
              'inflection-scheme-modal__tr--plural-first':
                key === personOrder.indexOf('first_person_plural')
            }"
          >
            <td class="inflection-scheme-modal__td w-25" scope="row">{{ row.person }}</td>
            <td class="inflection-scheme-modal__td">{{ row.prefix }} {{ row.inflexion }}</td>
          </tr>
          <tr
            v-if="key === personOrder.indexOf('third_person_singular')"
            :key="`divider-${key}`"
            aria-hidden="true"
          >
            <td class="inflection-scheme-modal__table-delimiter" colspan="2"></td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>
